import React, { useState, useEffect, useRef } from 'react';
import plumeUrl from '@/assets/plume.png';

interface TypingIndicatorProps {
  /** L'agent écrit : plume animée + timer. La plume disparaît dès que `isActive` repasse à false. */
  isActive: boolean;
  delayMs?: number;
  /**
   * Horodatage (ms, `Date.now()`) du DÉBUT du tour agent courant, fourni par le parent.
   * Le timer affiché compte `now - startedAt` — il reste donc continu sur tout le tour,
   * même quand l'indicateur clignote (deltas) ou s'efface brièvement (tool/thinking).
   * Purement client (jamais persisté) : il disparaît avec l'indicateur quand le tour finit.
   */
  startedAt?: number | null;
}

/** "12s", "1m 4s", "12m 7s" — secondes seules sous 1 min, puis "Xm Ys". */
function formatElapsed(totalSeconds: number): string {
  const s = totalSeconds % 60;
  const m = Math.floor(totalSeconds / 60);
  return m === 0 ? `${s}s` : `${m}m ${s}s`;
}

export const TypingIndicator: React.FC<TypingIndicatorProps> = ({
  isActive,
  delayMs = 100,
  startedAt = null,
}) => {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Le délai ne s'applique qu'à l'ACTIVATION (anti-flash sur un tour très court) — surtout
  // PAS à chaque delta : la plume doit rester affichée en continu sous le texte tant que
  // l'agent écrit. Déps volontairement limitées à [isActive, delayMs] (un re-déclenchement
  // par delta re-cacherait la plume à chaque token reçu — le bug qu'on corrige ici).
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (isActive) {
      timerRef.current = setTimeout(() => setVisible(true), delayMs);
    } else {
      setVisible(false);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isActive, delayMs]);

  // Tick 1 Hz tant que l'indicateur est visible — sert uniquement à rafraîchir
  // l'affichage du timer (la valeur est recalculée depuis `startedAt` au render).
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!visible || startedAt == null) return undefined;
    const id = setInterval(() => forceTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [visible, startedAt]);

  // Plume animée + timer UNIQUEMENT pendant que l'agent écrit. Dès que le tour se termine
  // (`isActive` → false), `visible` repasse à false → la plume disparaît (rien à l'arrêt).
  if (!visible) return null;

  const showTimer = startedAt != null;
  const elapsedSeconds =
    startedAt != null ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : 0;

  return (
    // Pas de padding vertical propre : la plume est centrée par le slot de pied à
    // hauteur fixe de StreamingTimeline (`min-h-[24px] items-center`), partagé avec
    // les boutons d'action. Un `py-1` ici ferait dépasser la plume (20px + 8px > 24px)
    // → la ligne grandirait pendant l'écriture puis rétrécirait à l'arrivée des boutons.
    <div className="flex items-center gap-2">
      {/* w-auto : la boîte épouse la largeur intrinsèque de la plume (image rognée sur
          son contour) → son bord gauche s'aligne sur le texte des messages (pas de px). */}
      <img
        src={plumeUrl}
        alt=""
        aria-hidden
        className="h-[20px] w-auto opacity-90 [transform-origin:50%_100%] [animation:plume-write_1.5s_ease-in-out_infinite]"
      />
      {showTimer && (
        <span className="text-[13px] tabular-nums text-grey-400">{formatElapsed(elapsedSeconds)}</span>
      )}
    </div>
  );
};
